import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { X } from 'react-bootstrap-icons';
import { load_user } from '../actions/auth';
import { resolveProfilePic } from '../utils/profile';

function Profile({ trigger, setTrigger, load_user }) {
  const inputRef = useRef();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company_name, setCompanyName] = useState('');
  const [location, setLocation] = useState('');
  const [profilePicUrl, setProfilePicUrl] = useState('');
  const [errorMessages, setErrorMessages] = useState([]);
  const [successMessages, setSuccessMessages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!trigger) return undefined;
    const fetchProfile = async () => {
      const response = await fetch(`${process.env.REACT_APP_AUTH_URL}/auth/users/me/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${localStorage.getItem('access')}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorMessages([data.detail || 'Failed to load profile']);
      } else {
        setFirstName(data.first_name || '');
        setLastName(data.last_name || '');
        setCompanyName(data.company_name || '');
        setLocation(data.location || '');
        setProfilePicUrl(data.profile_pic_url || '');
        setErrorMessages([]);
      }
    };
    fetchProfile();
  }, [trigger]);

  useEffect(() => {
    if (!trigger) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setTrigger(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [trigger, setTrigger]);

  const updateProfile = async () => {
    setSaving(true);
    const response = await fetch(`${process.env.REACT_APP_AUTH_URL}/auth/users/me/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `JWT ${localStorage.getItem('access')}`,
      },
      body: JSON.stringify({
        first_name: firstName,
        last_name: lastName,
        company_name: company_name,
        location: location,
      }),
    });
    const data = await response.json();
    setSaving(false);
    if (!response.ok) {
      setErrorMessages([data.detail || 'Update failed']);
      setSuccessMessages([]);
    } else {
      setSuccessMessages(['Profile updated successfully']);
      setErrorMessages([]);
      load_user();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await updateProfile();
  };

  const handleProfilePic = async (e) => {
    e.preventDefault();
    if (!inputRef.current?.files?.[0]) {
      setErrorMessages(['Please choose an image file']);
      return;
    }
    const formData = new FormData();
    formData.append('file', inputRef.current.files[0]);
    const response = await fetch(
      `${process.env.REACT_APP_AUTH_URL}/auth/users/me/upload-image/`,
      {
        method: 'POST',
        headers: {
          Authorization: `JWT ${localStorage.getItem('access')}`,
        },
        body: formData,
      }
    );
    const data = await response.json();
    if (!response.ok) {
      setErrorMessages([data.detail || 'Upload failed']);
      setSuccessMessages([]);
    } else {
      setProfilePicUrl(data.profile_pic_url || '');
      setSuccessMessages(['Profile picture updated']);
      setErrorMessages([]);
      load_user();
    }
  };

  const onFilePicked = async (e) => {
    if (e.target.files?.[0]) {
      await handleProfilePic(e);
    }
  };

  if (!trigger) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={() => setTrigger(false)}
      role="presentation"
    >
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border-subtle bg-surface p-6 text-text-primary shadow-composer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-dialog-title"
      >
        <button
          type="button"
          className="absolute right-3 top-3 rounded-lg p-1.5 text-text-muted hover:bg-surface-hover hover:text-text-primary"
          onClick={() => setTrigger(false)}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <h2 id="profile-dialog-title" className="mb-5 text-xl font-semibold">
          Edit profile
        </h2>

        {errorMessages.map((message, index) => (
          <div
            key={`err-${index}`}
            className="mb-3 rounded-lg border border-red-900/50 bg-[#5c1a1a] px-3 py-2 text-sm text-[#ffb4b4]"
          >
            {message}
          </div>
        ))}
        {successMessages.map((message, index) => (
          <div
            key={`ok-${index}`}
            className="mb-3 rounded-lg border border-accent/40 bg-accent/15 px-3 py-2 text-sm text-accent"
          >
            {message}
          </div>
        ))}

        <div className="mb-5 flex flex-col items-center">
          <button
            type="button"
            className="group relative h-24 w-24 overflow-hidden rounded-full border-2 border-border-subtle"
            onClick={() => inputRef.current?.click()}
            title="Change photo"
          >
            <img
              src={resolveProfilePic(profilePicUrl)}
              alt="Profile"
              className="h-full w-full object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-xs font-medium text-white opacity-0 transition group-hover:opacity-100">
              Change
            </span>
          </button>
          <input
            type="file"
            className="hidden"
            id="file"
            name="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            ref={inputRef}
            onChange={onFilePicked}
          />
          <p className="mt-2 text-xs text-text-muted">Click avatar to upload a new photo</p>
        </div>

        <form className="space-y-3" onSubmit={handleSubmit}>
          <Field label="First name" id="firstName" value={firstName} onChange={setFirstName} />
          <Field label="Last name" id="lastName" value={lastName} onChange={setLastName} />
          <Field
            label="Institute"
            id="company_name"
            value={company_name}
            onChange={setCompanyName}
          />
          <Field label="Location" id="location" value={location} onChange={setLocation} />
          <button
            className="mt-2 w-full rounded-xl bg-accent py-2.5 text-sm font-medium text-white transition hover:bg-accent-hover disabled:opacity-60"
            type="submit"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, id, value, onChange }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-text-muted">
        {label}
      </label>
      <input
        type="text"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-border-subtle bg-surface-alt px-3 py-2.5 text-sm text-text-primary outline-none focus:border-accent"
      />
    </div>
  );
}

export default connect(null, { load_user })(Profile);
